import { ref, computed } from 'vue'

const USER_CACHE_PREFIX = 'project_users_'
const USER_CACHE_VERSION = 1
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours
export const MAX_CACHEABLE_USERS = 200

export function useUserCache() {
  function getCacheKey(projectId) {
    return `${USER_CACHE_PREFIX}${projectId}_v${USER_CACHE_VERSION}`
  }

  function getCachedUsers(projectId) {
    if (!projectId) return null

    try {
      const cacheKey = getCacheKey(projectId)
      const cached = localStorage.getItem(cacheKey)
      if (!cached) return null

      const data = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is expired
      if (data.expiresAt && now > data.expiresAt) {
        localStorage.removeItem(cacheKey)
        return null
      }

      return data.users || null
    } catch (error) {
      console.error('Error reading user cache:', error)
      return null
    }
  }

  function setCachedUsers(projectId, users) {
    if (!projectId || !users) return

    // Only cache if user count is below threshold
    if (users.length >= MAX_CACHEABLE_USERS) {
      return
    }

    try {
      const cacheKey = getCacheKey(projectId)
      const data = {
        users: users,
        cachedAt: Date.now(),
        expiresAt: Date.now() + CACHE_EXPIRY_MS,
      }
      localStorage.setItem(cacheKey, JSON.stringify(data))
    } catch (error) {
      console.error('Error writing user cache:', error)
      // If storage is full, try to clear old caches
      clearExpiredCaches()
      try {
        const cacheKey = getCacheKey(projectId)
        const data = {
          users: users,
          cachedAt: Date.now(),
          expiresAt: Date.now() + CACHE_EXPIRY_MS,
        }
        localStorage.setItem(cacheKey, JSON.stringify(data))
      } catch (retryError) {
        console.error('Error writing user cache after cleanup:', retryError)
      }
    }
  }

  function clearExpiredCaches() {
    try {
      const now = Date.now()
      const keysToRemove = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(USER_CACHE_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (data.expiresAt && now > data.expiresAt) {
              keysToRemove.push(key)
            }
          } catch (e) {
            // Invalid cache entry, remove it
            keysToRemove.push(key)
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error('Error clearing expired caches:', error)
    }
  }

  function clearCache(projectId) {
    if (!projectId) return

    try {
      const cacheKey = getCacheKey(projectId)
      localStorage.removeItem(cacheKey)
    } catch (error) {
      console.error('Error clearing user cache:', error)
    }
  }

  function shouldUseCache(users) {
    return users && users.length > 0 && users.length < MAX_CACHEABLE_USERS
  }

  return {
    getCachedUsers,
    setCachedUsers,
    clearCache,
    clearExpiredCaches,
    shouldUseCache,
  }
}
